package co.maytes.standupbot.dashboard.features.releases

import co.maytes.standupbot.shared.dto.ship.ShipEventDto
import co.maytes.standupbot.shared.format.ReleaseTimeFormat
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.daysUntil

data class DayGroup(val date: LocalDate, val label: String, val events: List<ShipEventDto>)

data class RepoGroup(val repo: String, val events: List<ShipEventDto>) {
    val latest: ShipEventDto get() = events.first()
    val releaseCount: Int get() = events.count { it.isRelease }
}

object ReleaseFeedGroups {
    private const val SHORT_NAME_LENGTH = 3
    private const val MINUTES_PER_HOUR = 60L
    private const val MINUTES_PER_DAY = 1440L
    private const val DATE_AFTER_DAYS = 30
    private const val EXPANDED_WHEN_FEW = 3

    fun byDay(
        events: List<ShipEventDto>,
        zone: TimeZone,
        today: LocalDate,
    ): List<DayGroup> {
        val groups = linkedMapOf<LocalDate, MutableList<ShipEventDto>>()
        events.forEach { groups.getOrPut(ReleaseTimeFormat.localDay(it.occurredAt, zone)) { mutableListOf() }.add(it) }
        return groups.map { (date, items) -> DayGroup(date, dayLabel(date, today), items) }
    }

    fun byRepo(events: List<ShipEventDto>): List<RepoGroup> =
        events
            .groupBy { it.repo }
            .map { (repo, items) -> RepoGroup(repo, items.sortedByDescending { it.occurredAt }) }
            .sortedWith(compareByDescending<RepoGroup> { it.latest.occurredAt }.thenBy { it.repo.lowercase() })

    fun dayLabel(
        date: LocalDate,
        today: LocalDate,
    ): String =
        when (date.daysUntil(today)) {
            0 -> "Today"
            1 -> "Yesterday"
            else -> "${shortName(date.dayOfWeek.name)} ${date.dayOfMonth} ${shortName(date.month.name)}"
        }

    fun relative(
        now: Instant,
        then: Instant,
        zone: TimeZone,
    ): String {
        val minutes = (now - then).inWholeMinutes
        return when {
            minutes < 1 -> "Just now"
            minutes < MINUTES_PER_HOUR -> "$minutes min ago"
            minutes < MINUTES_PER_DAY -> "${minutes / MINUTES_PER_HOUR} h ago"
            else -> {
                val thenDay = ReleaseTimeFormat.localDay(then, zone)
                val days = thenDay.daysUntil(ReleaseTimeFormat.localDay(now, zone))
                when {
                    days <= 1 -> "Yesterday"
                    days < DATE_AFTER_DAYS -> "$days d ago"
                    else -> "${thenDay.dayOfMonth} ${shortName(thenDay.month.name)} ${thenDay.year}"
                }
            }
        }
    }

    fun relativeInSentence(label: String): String =
        when {
            label == "Yesterday" -> "yesterday"
            label == "Just now" -> "just now"
            label.endsWith(" ago") -> label
            else -> "on $label"
        }

    fun defaultRepoOpen(
        index: Int,
        total: Int,
        searching: Boolean,
    ): Boolean = searching || total <= EXPANDED_WHEN_FEW || index == 0

    private fun shortName(constantName: String): String =
        constantName.lowercase().replaceFirstChar { it.uppercase() }.take(SHORT_NAME_LENGTH)
}
